// Web Vitals 性能监控 — 采集核心性能指标 + 上报
// 指标：LCP / INP / CLS / FCP / TTFb
// 参考：https://web.dev/vitals/

import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from "web-vitals";

export interface WebVitalsReport {
  lcp?: number;   // Largest Contentful Paint (ms)
  inp?: number;   // Interaction to Next Paint (ms)
  /** 后端列名是 fid（历史命名），上报时同时带 fid = inp，否则交互延迟这一列永远是 0 */
  fid?: number;
  cls?: number;   // Cumulative Layout Shift
  fcp?: number;   // First Contentful Paint (ms)
  ttfb?: number;  // Time to First Byte (ms)
  url: string;
  userAgent: string;
  timestamp: number;
  [key: string]: unknown; // index signature for apiReportVitals
}

const STORAGE_KEY = "web_vitals_report";

/**
 * 获取最近一次采集的 Web Vitals 数据（供管理后台展示）
 */
export function getLastVitals(): WebVitalsReport | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * 初始化 Web Vitals 采集
 *
 * **上报时机（2026-09-23 修正）**：此前是「每次指标更新就上报」——FCP/TTFB 发生得最早，
 * 于是绝大多数入库记录里 LCP/CLS/INP 都还是 0，监控页看到的就是「LCP 0ms」这类假数据。
 * 现改为**只在页面隐藏/卸载时上报一次**（此时 LCP/CLS/INP 都已产生），并给 fid 打上 inp 的别名。
 *
 * @param reportCallback 上报回调（页面隐藏时触发一次；多次隐藏只报一次，避免重复计数）
 */
export function initWebVitals(reportCallback?: (report: WebVitalsReport) => void): void {
  const report: WebVitalsReport = {
    url: window.location.pathname,
    userAgent: navigator.userAgent,
    timestamp: Date.now(),
  };

  const persist = () => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(report)); } catch {}
  };

  const update = (m: Metric) => {
    switch (m.name) {
      case "LCP": report.lcp = Math.round(m.value); break;
      case "INP":
        report.inp = Math.round(m.value);
        // 网关/表用的是 fid 列（历史命名），不带这个别名则交互延迟永远写不进去
        report.fid = report.inp;
        break;
      case "CLS": report.cls = Math.round(m.value * 1000) / 1000; break;
      case "FCP": report.fcp = Math.round(m.value); break;
      case "TTFB": report.ttfb = Math.round(m.value); break;
    }
    persist();
  };

  onLCP(update);
  onINP(update);
  onFCP(update);
  onCLS(update);
  onTTFB(update);

  let reported = false;
  const flush = () => {
    persist();
    if (reported) return;
    reported = true;
    reportCallback?.(report);
  };

  // pagehide 覆盖关闭/前进后退；visibilitychange 覆盖切后台（移动端可能不触发 pagehide）
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });

  console.log("[webVitals] 性能监控已启动（页面隐藏时上报一次）");
}

/**
 * 获取性能等级（good / needs-improvement / poor）
 */
export function getVitalsGrade(metric: "lcp" | "inp" | "cls" | "fcp" | "ttfb", value: number): "good" | "needs-improvement" | "poor" {
  const thresholds: Record<string, [number, number]> = {
    lcp: [2500, 4000],
    inp: [100, 300],
    cls: [0.1, 0.25],
    fcp: [1800, 3000],
    ttfb: [800, 1800],
  };
  const [good, poor] = thresholds[metric] || [0, 0];
  if (value <= good) return "good";
  if (value <= poor) return "needs-improvement";
  return "poor";
}

/**
 * 格式化显示值
 */
export function formatVitalsValue(metric: string, value: number): string {
  if (metric === "cls") return value.toFixed(3);
  return `${value}ms`;
}
